import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";

function Metric({ label, value }) {
  return <div className="card-brutal p-4 bg-white"><div className="font-mono text-2xl font-bold">{value}</div><div className="text-xs text-muted-foreground uppercase">{label}</div></div>;
}

export default function Admin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [reports, setReports] = useState([]);
  const [resolutions, setResolutions] = useState([]);
  const [calls, setCalls] = useState([]);
  const [users, setUsers] = useState([]);
  const [messages, setMessages] = useState([]);

  const loadAll = () => {
    api.get("/admin/metrics").then(({ data }) => setMetrics(data)).catch(() => {});
    api.get("/admin/reports").then(({ data }) => setReports(data)).catch(() => {});
    api.get("/admin/resolutions").then(({ data }) => setResolutions(data)).catch(() => {});
    api.get("/admin/calls").then(({ data }) => setCalls(data)).catch(() => {});
    api.get("/admin/users").then(({ data }) => setUsers(data)).catch(() => {});
    api.get("/admin/messages").then(({ data }) => setMessages(data)).catch(() => {});
  };

  useEffect(() => {
    if (loading) return;
    if (!user || user.role !== "admin") { navigate("/"); return; }
    loadAll();
  }, [user, loading, navigate]);

  const moderate = async (action, target_id) => {
    await api.post("/admin/moderate", { action, target_id });
    toast.success(action.replace(/_/g, " ")); loadAll();
  };
  const decide = async (resolution_id, outcome) => {
    await api.post(`/resolutions/${resolution_id}/decide`, { outcome });
    toast.success("Decision recorded"); loadAll();
  };
  const resolveReport = async (id) => { await api.post(`/admin/reports/${id}/resolve`); loadAll(); };

  const [cleanOpen, setCleanOpen] = useState(false);
  const [cleanPreview, setCleanPreview] = useState(null);
  const [cleaning, setCleaning] = useState(false);

  const openClean = async () => {
    try {
      const { data } = await api.post("/admin/clean-test-data?execute=false");
      setCleanPreview(data);
      setCleanOpen(true);
    } catch { toast.error("Could not scan for test data"); }
  };
  const runClean = async () => {
    setCleaning(true);
    try {
      const { data } = await api.post("/admin/clean-test-data?execute=true");
      toast.success(`Removed ${data.deleted_users} test users and ${data.deleted_calls} test Calls`);
      setCleanOpen(false); setCleanPreview(null); loadAll();
    } catch { toast.error("Cleanup failed"); }
    finally { setCleaning(false); }
  };

  if (!user || user.role !== "admin") return null;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <h1 className="font-display text-3xl font-bold">Admin</h1>
        <button data-testid="clean-test-data-btn" onClick={openClean}
          className="h-10 px-4 border-2 border-ink bg-missed text-white font-bold btn-press shadow-brutal-sm flex items-center gap-2 text-sm">
          <Trash2 className="w-4 h-4" /> Clean test data
        </button>
      </div>
      {metrics && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <Metric label="Total Calls" value={metrics.total_calls} />
          <Metric label="Calls Today" value={metrics.calls_today} />
          <Metric label="Users" value={metrics.total_users} />
          <Metric label="Guest Calls" value={metrics.guest_calls} />
          <Metric label="Resolved" value={metrics.resolved_calls} />
          <Metric label="Shares" value={metrics.shares} />
          <Metric label="Guest→Signup" value={`${metrics.guest_conversion_pct}%`} />
          <Metric label="Open Reports" value={metrics.reports_awaiting_review} />
        </div>
      )}

      <Tabs defaultValue="reports">
        <TabsList className="border-2 border-ink bg-white">
          <TabsTrigger value="reports" data-testid="admin-tab-reports">Reports</TabsTrigger>
          <TabsTrigger value="resolutions" data-testid="admin-tab-resolutions">Resolutions</TabsTrigger>
          <TabsTrigger value="calls" data-testid="admin-tab-calls">Calls</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users">Users</TabsTrigger>
          <TabsTrigger value="messages" data-testid="admin-tab-messages">Messages</TabsTrigger>
        </TabsList>

        <TabsContent value="reports" className="space-y-2 mt-4">
          {reports.map((r) => (
            <div key={r.id} className="card-brutal p-3 bg-white text-sm">
              <div className="flex justify-between"><b>{r.reason}</b><span className="text-xs text-muted-foreground">{r.target_type}: {r.target_id}</span></div>
              {r.detail && <p className="text-muted-foreground">{r.detail}</p>}
              <div className="flex gap-2 mt-2">
                {r.target_type === "call" && <button onClick={() => moderate("hide_call", r.target_id)} className="text-xs border-2 border-ink px-2 py-1 btn-press shadow-brutal-sm bg-missed text-white">Hide Call</button>}
                {r.target_type === "comment" && <button onClick={() => moderate("remove_comment", r.target_id)} className="text-xs border-2 border-ink px-2 py-1 btn-press shadow-brutal-sm bg-missed text-white">Remove</button>}
                <button onClick={() => resolveReport(r.id)} className="text-xs border-2 border-ink px-2 py-1 btn-press shadow-brutal-sm bg-white">Mark reviewed</button>
              </div>
            </div>
          ))}
          {reports.length === 0 && <p className="text-muted-foreground">No open reports.</p>}
        </TabsContent>

        <TabsContent value="resolutions" className="space-y-2 mt-4">
          {resolutions.map((r) => (
            <div key={r.id} className="card-brutal p-3 bg-white text-sm">
              <div className="flex justify-between"><Link to={`/c/${r.call_id}`} className="font-mono text-brand underline">{r.call_id}</Link><b>claims: {r.outcome}</b></div>
              <p className="text-muted-foreground">{r.explanation}</p>
              <div className="flex gap-2 mt-2">
                {["called_it", "missed_it", "partial", "ambiguous"].map((o) => (
                  <button key={o} onClick={() => decide(r.id, o)} className="text-xs border-2 border-ink px-2 py-1 btn-press shadow-brutal-sm bg-locked">{o}</button>
                ))}
              </div>
            </div>
          ))}
          {resolutions.length === 0 && <p className="text-muted-foreground">No pending resolutions.</p>}
        </TabsContent>

        <TabsContent value="calls" className="space-y-2 mt-4">
          {calls.map((c) => (
            <div key={c.short_id} className="card-brutal p-3 bg-white text-sm flex justify-between items-center gap-2">
              <Link to={`/c/${c.short_id}`} className="truncate flex-1">{c.prediction_text || "(sealed)"}</Link>
              <button onClick={() => moderate(c.hidden ? "unhide_call" : "hide_call", c.short_id)} className="text-xs border-2 border-ink px-2 py-1 btn-press shadow-brutal-sm bg-white shrink-0">{c.hidden ? "Unhide" : "Hide"}</button>
              <button onClick={() => moderate("mark_invalid", c.short_id)} className="text-xs border-2 border-ink px-2 py-1 btn-press shadow-brutal-sm bg-missed text-white shrink-0">Invalid</button>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="users" className="space-y-2 mt-4">
          {users.map((u) => (
            <div key={u.user_id} className="card-brutal p-3 bg-white text-sm flex justify-between items-center">
              <span>@{u.username} · {u.role}{u.restricted && " · restricted"}</span>
              <button onClick={() => moderate(u.restricted ? "unrestrict_user" : "restrict_user", u.user_id)} className="text-xs border-2 border-ink px-2 py-1 btn-press shadow-brutal-sm bg-white">{u.restricted ? "Unrestrict" : "Restrict"}</button>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="messages" className="space-y-2 mt-4">
          {messages.map((m) => (
            <div key={m.id} data-testid={`message-${m.id}`} className="card-brutal p-3 bg-white text-sm">
              <div className="flex justify-between items-center gap-2">
                <span className="font-bold">{m.name} · <a href={`mailto:${m.email}`} className="text-brand underline">{m.email}</a></span>
                <span className="text-xs font-mono border-2 border-ink px-2 py-0.5 bg-secondary">{m.category}</span>
              </div>
              <p className="mt-2 whitespace-pre-wrap">{m.message}</p>
              <div className="text-xs text-muted-foreground mt-2">
                {new Date(m.created_at).toLocaleString()} · email {m.emailed ? "sent" : "not sent"}
              </div>
            </div>
          ))}
          {messages.length === 0 && <p className="text-muted-foreground">No contact messages yet.</p>}
        </TabsContent>
      </Tabs>

      <Dialog open={cleanOpen} onOpenChange={setCleanOpen}>        <DialogContent className="card-brutal border-2 border-ink sm:max-w-md" data-testid="clean-test-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl font-bold">Clean test data?</DialogTitle></DialogHeader>
          <DialogDescription className="text-sm text-muted-foreground">
            This permanently removes demo/QA records only. Real accounts and Calls are preserved.
          </DialogDescription>
          {cleanPreview && (
            <div className="border-2 border-ink bg-secondary p-4 space-y-2 text-sm">
              <div className="flex justify-between"><span>Test users to delete</span><b data-testid="clean-users-count">{cleanPreview.test_users}</b></div>
              <div className="flex justify-between"><span>Test Calls to delete</span><b data-testid="clean-calls-count">{cleanPreview.test_calls}</b></div>
              <div className="flex justify-between"><span>Real users remaining</span><b className="text-called">{cleanPreview.remaining_users}</b></div>
              {cleanPreview.sample_users?.length > 0 && (
                <div className="pt-1">
                  <div className="text-xs text-muted-foreground mb-1">Sample (verify these aren't real):</div>
                  <div className="font-mono text-xs space-y-0.5 max-h-32 overflow-auto">
                    {cleanPreview.sample_users.map((u, i) => <div key={i}>@{u.username} · {u.email}</div>)}
                  </div>
                </div>
              )}
            </div>
          )}
          <button data-testid="confirm-clean-btn" disabled={cleaning || !cleanPreview?.test_users}
            onClick={runClean}
            className="w-full h-12 border-2 border-ink bg-missed text-white font-bold btn-press shadow-brutal-sm disabled:opacity-40">
            {cleaning ? "Cleaning…" : `Delete ${cleanPreview?.test_users || 0} users & ${cleanPreview?.test_calls || 0} Calls`}
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
